import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../axios';
import { InvoiceFormData, EventUpdatePayload, getErrorResponse } from '@src/types';

export const useEventsALLQuery = () => {
    return useQuery({
        queryKey: ['events'],
        queryFn: async () => {
            try {
                const { data, status } = await api.get(`/events`);
                return { data: { ...data }, status };
            } catch (error) {
                throw error;
            }
        },
    });
};

export const usePreEventsQuery = (subCategoryId: string, year: string, quarter: string, search: string, page: number, limit: number) => {
    return useQuery({
        queryKey: ['events', subCategoryId, year, quarter, search, page, limit],
        queryFn: async ({ queryKey }: { queryKey: [string, string, string, string, string, number, number] }) => {
            const [, subCategoryId, year, quarter, search, page, limit] = queryKey;
            try {
                const { data } = await api.get(
                    `/events/pre-event?subCategoryId=${subCategoryId}&year=${year}&quarter=${quarter}&search=${search}&page=${page}&limit=${limit}`,
                );

                return data;
            } catch (error) {
                throw error;
            }
        },
    });
};

export const usePostEventsQuery = (subCategoryId: string, year: string, quarter: string, search: string, page: number, limit: number) => {
    return useQuery({
        queryKey: ['postEvents', subCategoryId, year, quarter, search, page, limit],
        queryFn: async ({ queryKey }: { queryKey: [string, string, string, string, string, number, number] }) => {
            const [, subCategoryId, year, quarter, search, page, limit] = queryKey;
            try {
                const { data } = await api.get(
                    `/events/post-event?subCategoryId=${subCategoryId}&year=${year}&quarter=${quarter}&search=${search}&page=${page}&limit=${limit}`,
                );
                return data;
            } catch (error) {
                throw error;
            }
        },
    });
};
export const useEventsByIdQuery = (id: number | string) => {
    return useQuery({
        queryKey: ['eventsId', id],
        queryFn: async ({ queryKey }: { queryKey: [string, number | string] }) => {
            const [, id] = queryKey;
            try {
                const { data } = await api.get(`/events/${id}`);
                return data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    });
};

export const useCreditNumberByIdQuery = () => {
    return useMutation({
        mutationFn: async (postEventCreditNumber: { eventId: number | string; creditNumber: string | undefined }) => {
            try {
                const { data, status } = await api.patch(`/events/${postEventCreditNumber.eventId}/credit-number`, postEventCreditNumber);
                return {
                    data,
                    status,
                };
            } catch (error) {
                return getErrorResponse(error);
            }
        },
    });
};

export const useUpdateEventNameByIdQuery = () => {
    return useMutation({
        mutationFn: async (postEventName: { eventId: number | string; name: string | undefined }) => {
            try {
                const { data, status } = await api.patch(`/events/${postEventName.eventId}/name`, postEventName);
                return {
                    data,
                    status,
                };
            } catch (error) {
                return getErrorResponse(error);
            }
        },
    });
};

export const useCreatePerEventQuery = () => {
    return useMutation({
        mutationFn: async (preEvent: EventUpdatePayload) => {
            try {
                const { data, status } = await api.post(`/events`, preEvent);
                return {
                    data,
                    status,
                };
            } catch (error) {
                return getErrorResponse(error);
            }
        },
    });
};

export const useUpdatePerEventQuery = () => {
    return useMutation({
        mutationFn: async ({ eventId, preEvent }: { eventId: number; preEvent: EventUpdatePayload }) => {
            preEvent.id = eventId;
            try {
                const { data, status } = await api.patch(`/events/${eventId}`, preEvent);
                return {
                    data,
                    status,
                };
            } catch (error) {
                return getErrorResponse(error);
            }
        },
    });
};

export const useDeleteEventQuery = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (eventId: number) => {
            try {
                const { data, status } = await api.delete(`/events/${eventId}`);
                return {
                    data,
                    status,
                };
            } catch (error) {
                return getErrorResponse(error);
            }
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
};

export const useUpdateEventQuery = () => {
    return useMutation({
        mutationFn: async ({ eventId, eventData }: { eventId: number; eventData: EventUpdatePayload }) => {
            try {
                const { data, status } = await api.patch(`/events/${eventId}`, eventData);
                return {
                    data,
                    status,
                };
            } catch (error) {
                return getErrorResponse(error);
            }
        },
    });
};

export const useInvoiceByEventIdQuery = () => {
    return useMutation({
        mutationFn: async ({ eventId }: { eventId: number }) => {
            try {
                const { data } = await api.get(`/events/${eventId}/invoice`);
                return data;
            } catch (error) {
                throw error;
            }
        },
    });
};

export const useReceiptByEventIdQuery = () => {
    return useMutation({
        mutationFn: async ({ eventId }: { eventId: number }) => {
            try {
                const { data } = await api.get(`/events/${eventId}/receipt-pdf`, {
                    headers: { 'Content-Type': 'application/pdf' },
                    responseType: 'blob',
                });
                return data;
            } catch (error) {
                throw error;
            }
        },
    });
};

export const useInvoicePdfByEventIdQuery = () => {
    return useMutation({
        mutationFn: async ({ eventId }: { eventId: number }) => {
            try {
                const { data } = await api.get(`/events/${eventId}/invoice-pdf`, {
                    headers: { 'Content-Type': 'application/pdf' },
                    responseType: 'blob',
                });
                return data;
            } catch (error) {
                throw error;
            }
        },
    });
};

export const useAddInvoiceByEventIdQuery = () => {
    return useMutation({
        mutationFn: async ({ eventId, invoiceData }: { eventId: number; invoiceData: InvoiceFormData }) => {
            try {
                const { data } = await api.post(`/events/${eventId}/invoice`, invoiceData);
                return data;
            } catch (error) {
                throw error;
            }
        },
    });
};
